import React, { useState } from "react";
import { toast } from "sonner";
import { UserPlus, Loader2, Check, UserCog, Target, AlertTriangle, Copy, Building2, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { Modal, Field, INP_STYLE, ROLE_KEYS, ROLE_LABEL, ROLE_DESC } from "./shared";

export function CreateUserModal({ t, clients, matrix, onClose, onDone }) {
  const [f, setF] = useState({ name: "", email: "", role: "team_member", client_id: "", password: "", team_role: "viewer", client_scope: "" });
  const [busy, setBusy] = useState(false);
  const needsClient = f.role === "client" || f.role === "client_staff";
  const isTeam = f.role === "team_member";
  const teamRoleOptions = (matrix?.roles || []).filter((r) => r.key !== "super_admin");
  const submit = async () => {
    if (!f.name || !f.email) return toast.error(t("Aktion fehlgeschlagen"));
    if (isTeam && !f.client_scope) return toast.error(t("Bitte Kundenzugriff festlegen"));
    setBusy(true);
    try {
      const body = { name: f.name, email: f.email, role: f.role, password: f.password || undefined, client_id: needsClient ? f.client_id : undefined, team_role: isTeam ? f.team_role : undefined, client_scope: isTeam ? f.client_scope : undefined, send_invite: false };
      const { data } = await api.post("/admin/users", body);
      toast.success(data?.prepared ? t("Zugang vorbereitet · keine E-Mail versendet") : t("Benutzer angelegt · Zugang erzeugt"));
      onDone(data);
    } catch (e) { toast.error(e.response?.data?.detail || t("Aktion fehlgeschlagen")); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={t("Benutzer anlegen")} onClose={onClose} testid="create-user-modal" wide>
      <Field label={t("Name")}><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="create-name" className="inp" /></Field>
      <Field label={t("E-Mail")}><input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="create-email" className="inp" /></Field>
      <Field label={t("Kontotyp")}>
        <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="create-role" className="inp">
          {ROLE_KEYS.map((r) => <option key={r} value={r}>{t(ROLE_LABEL[r])}</option>)}
        </select>
      </Field>
      {isTeam && (
        <Field label={t("Team-Rolle")}>
          <select value={f.team_role} onChange={(e) => setF({ ...f, team_role: e.target.value })} data-testid="create-team-role" className="inp">
            {teamRoleOptions.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </Field>
      )}
      {isTeam && (
        <Field label={t("Kundenzugriff (Pflicht)")}>
          <select value={f.client_scope} onChange={(e) => setF({ ...f, client_scope: e.target.value })} data-testid="create-client-scope" className="inp">
            <option value="">{t("Bitte Kundenzugriff festlegen")}</option>
            <option value="keine">{t("Keine Kunden")}</option>
            <option value="alle">{t("Alle Kunden")}</option>
          </select>
        </Field>
      )}
      {needsClient && (
        <Field label={t("Kunde")}>
          <select value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} data-testid="create-client" className="inp">
            <option value="">{t("Kunde wählen")}</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
        </Field>
      )}
      <Field label={t("Passwort (optional)")}>
        <input type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="create-password" className="inp" placeholder={t("Leer lassen für automatisches Temp-Passwort")} />
      </Field>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{t("Abbrechen")}</button>
        <button onClick={submit} disabled={busy} data-testid="create-submit" className="bg-[#A3E635] text-black font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} {t("Erstellen")}</button>
      </div>
      <style>{INP_STYLE}</style>
    </Modal>
  );
}

export function EditUserModal({ t, user, onClose, onDone }) {
  const [name, setName] = useState(user.name || "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.put(`/users/${user.id}`, { name }); toast.success(t("Gespeichert")); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Aktion fehlgeschlagen")); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={t("Benutzer bearbeiten")} onClose={onClose} testid="edit-user-modal">
      <Field label={t("Name")}><input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-name" className="inp" /></Field>
      <Field label={t("E-Mail")}><input value={user.email} disabled className="inp opacity-60" /></Field>
      <Field label={t("Kontotyp")}><input value={t(ROLE_LABEL[user.role] || "Unbekannt")} disabled className="inp opacity-60" /></Field>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{t("Abbrechen")}</button>
        <button onClick={submit} disabled={busy} data-testid="edit-submit" className="bg-[#A3E635] text-black font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {t("Speichern")}</button>
      </div>
      <style>{INP_STYLE}</style>
    </Modal>
  );
}

export function RoleModal({ t, user, onClose, onDone }) {
  const [role, setRole] = useState(user.role);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/admin/users/${user.id}/role`, { role }); toast.success(t("Rolle geändert")); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Aktion fehlgeschlagen")); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={t("Kontotyp ändern")} onClose={onClose} testid="role-modal">
      <p className="text-sm text-gray-400 mb-3">{user.name} · {user.email}</p>
      <Field label={t("Neuer Kontotyp")}>
        <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="role-select" className="inp">
          {ROLE_KEYS.map((r) => <option key={r} value={r}>{t(ROLE_LABEL[r])}</option>)}
        </select>
      </Field>
      <p className="text-[11px] text-gray-500">{t(ROLE_DESC[role])}</p>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{t("Abbrechen")}</button>
        <button onClick={submit} disabled={busy || role === user.role} data-testid="role-submit" className="bg-[#A3E635] text-black font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCog className="w-4 h-4" />} {t("Bestätigen")}</button>
      </div>
      <style>{INP_STYLE}</style>
    </Modal>
  );
}

export function TeamRoleModal({ t, user, matrix, onClose, onDone }) {
  const [role, setRole] = useState(user.team_role || "viewer");
  const [busy, setBusy] = useState(false);
  const options = (matrix?.roles || []).filter((r) => r.key !== "super_admin");
  const selected = options.find((r) => r.key === role);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/admin/users/${user.id}/team-role`, { team_role: role }); toast.success(t("Team-Rolle geändert")); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Aktion fehlgeschlagen")); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={t("Team-Rolle ändern")} onClose={onClose} testid="teamrole-modal" wide>
      <p className="text-sm text-gray-400 mb-3">{user.name} · {user.email}</p>
      <Field label={t("Interne Team-Rolle")}>
        <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="teamrole-select" className="inp">
          {options.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
      </Field>
      {selected && (
        <div className="rounded-lg border border-white/10 bg-black/30 p-3 mb-1">
          <p className="text-xs text-gray-400 mb-2">{selected.description}</p>
          <div className="flex flex-wrap gap-1.5">
            {selected.sections.map((s) => <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-gray-400">{matrix.sections.find((x) => x.key === s)?.label || s}</span>)}
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{t("Abbrechen")}</button>
        <button onClick={submit} disabled={busy || role === user.team_role} data-testid="teamrole-submit" className="bg-[#A3E635] text-black font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCog className="w-4 h-4" />} {t("Bestätigen")}</button>
      </div>
      <style>{INP_STYLE}</style>
    </Modal>
  );
}

export function AssignCompanyModal({ t, user, clients, onClose, onDone }) {
  const [cid, setCid] = useState(user.client_id || "");
  const [busy, setBusy] = useState(false);
  const sorted = [...(clients || [])].sort((a, b) => (a.company_name || "").localeCompare(b.company_name || ""));
  const submit = async () => {
    if (!cid) return toast.error(t("Bitte eine Firma auswählen."));
    setBusy(true);
    try { await api.post(`/admin/users/${user.id}/assign-company`, { client_id: cid }); toast.success(t("Firma zugewiesen")); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Aktion fehlgeschlagen")); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={t("Firma zuweisen")} onClose={onClose} testid="assign-company-modal" wide>
      <p className="text-sm text-gray-400 mb-1">{user.name} · {user.email}</p>
      <p className="text-[11px] text-gray-500 mb-4">{t("Aktuelle Firma")}: <span className="text-gray-300 font-semibold">{user.company_name || "—"}</span></p>
      <Field label={t("Firma / Kunde")}>
        <select value={cid} onChange={(e) => setCid(e.target.value)} data-testid="assign-company-select" className="inp">
          <option value="">{t("Firma wählen")}</option>
          {sorted.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
        </select>
      </Field>
      <p className="text-[11px] text-gray-500">{t("Der Benutzer wird der gewählten Firma zugeordnet und sieht nur deren Daten. Die Änderung wird protokolliert.")}</p>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{t("Abbrechen")}</button>
        <button onClick={submit} disabled={busy || cid === user.client_id} data-testid="assign-company-submit" className="bg-[#A3E635] text-black font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Building2 className="w-4 h-4" />} {t("Speichern")}</button>
      </div>
      <style>{INP_STYLE}</style>
    </Modal>
  );
}

const COCKPIT_SECTIONS = [
  { key: "overview", label: "Überblick" }, { key: "leads", label: "Anfragen" },
  { key: "tasks", label: "Aufgaben & Freigaben" }, { key: "support", label: "Support" },
  { key: "performance", label: "Besuche & Performance" }, { key: "invoices", label: "Rechnungen" },
  { key: "next-steps", label: "Nächste Schritte" }, { key: "report", label: "Monatsreport" },
];
export function ClientRightsModal({ t, user, onClose, onDone }) {
  const [role, setRole] = useState(user.role);
  const [sections, setSections] = useState(user.allowed_sections && user.allowed_sections.length ? user.allowed_sections : COCKPIT_SECTIONS.map((s) => s.key));
  const [busy, setBusy] = useState(false);
  const isStaff = role === "client_staff";
  const toggle = (k) => setSections((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const submit = async () => {
    if (isStaff && sections.length === 0) return toast.error(t("Bitte mindestens einen Bereich freigeben."));
    setBusy(true);
    try {
      if (role !== user.role) await api.post(`/admin/users/${user.id}/role`, { role });
      if (role === "client_staff") await api.put(`/users/${user.id}`, { allowed_sections: sections });
      toast.success(t("Rechte gespeichert"));
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || t("Aktion fehlgeschlagen")); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={t("Rechte & Rolle")} onClose={onClose} testid="client-rights-modal" wide>
      <p className="text-sm text-gray-400 mb-4">{user.name} · {user.email}</p>
      <Field label={t("Kontotyp")}>
        <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="rights-role-select" className="inp">
          <option value="client">{t("Kunden-Admin")}</option>
          <option value="client_staff">{t("Mitarbeiter")}</option>
        </select>
      </Field>
      {isStaff ? (
        <div className="rounded-lg border border-white/10 bg-black/30 p-3" data-testid="rights-sections">
          <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">{t("Freigegebene Bereiche")} ({sections.length})</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {COCKPIT_SECTIONS.map((s) => (
              <label key={s.key} data-testid={`rights-section-${s.key}`} className="flex items-center gap-2.5 py-1.5 px-1.5 rounded cursor-pointer hover:bg-white/[0.03]">
                <input type="checkbox" checked={sections.includes(s.key)} onChange={() => toggle(s.key)} className="accent-[#A3E635] w-4 h-4" />
                <span className="text-sm">{t(s.label)}</span>
              </label>
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-[#A3E635]/20 bg-[#A3E635]/[0.04] p-3 flex items-center gap-2.5">
          <Check className="w-4 h-4 text-[#A3E635] shrink-0" />
          <p className="text-xs text-gray-300">{t("Kunden-Admins haben Vollzugriff auf alle Bereiche ihrer eigenen Firma und können eigene Mitarbeiter verwalten.")}</p>
        </div>
      )}
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{t("Abbrechen")}</button>
        <button onClick={submit} disabled={busy} data-testid="rights-submit" className="bg-[#A3E635] text-black font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} {t("Rechte speichern")}</button>
      </div>
      <style>{INP_STYLE}</style>
    </Modal>
  );
}

export function ClientScopeModal({ t, user, clients, onClose, onDone }) {
  const [scope, setScope] = useState(["alle", "bestimmte", "keine"].includes(user.client_scope) ? user.client_scope : "keine");
  const [ids, setIds] = useState(user.scoped_client_ids || []);
  const [busy, setBusy] = useState(false);
  const toggle = (cid) => setIds((p) => (p.includes(cid) ? p.filter((x) => x !== cid) : [...p, cid]));
  const submit = async () => {
    if (scope === "bestimmte" && ids.length === 0) return toast.error(t("Bitte mindestens einen Kunden auswählen."));
    setBusy(true);
    try { await api.post(`/admin/users/${user.id}/client-scope`, { client_scope: scope, scoped_client_ids: scope === "bestimmte" ? ids : [] }); toast.success(t("Kunden-Zuweisung gespeichert")); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Aktion fehlgeschlagen")); }
    finally { setBusy(false); }
  };
  const opts = [
    { v: "alle", l: t("Alle Kunden"), d: t("Sieht alle Kunden im System.") },
    { v: "bestimmte", l: t("Bestimmte Kunden"), d: t("Sieht nur die ausgewählten Kunden. Echte Datenfilterung.") },
    { v: "keine", l: t("Keine Kunden"), d: t("Sieht keine Kundendaten (z. B. reine Buchhaltung im Aufbau).") },
  ];
  return (
    <Modal title={t("Kunden zuweisen")} onClose={onClose} testid="scope-modal" wide>
      <p className="text-sm text-gray-400 mb-4">{user.name} · {user.email}</p>
      <div className="space-y-2 mb-4">
        {opts.map((o) => (
          <button key={o.v} onClick={() => setScope(o.v)} data-testid={`scope-opt-${o.v}`} className={`w-full text-left rounded-lg border p-3 transition-colors ${scope === o.v ? "border-[#A3E635]/40 bg-[#A3E635]/[0.06]" : "border-white/10 bg-black/20 hover:border-white/20"}`}>
            <div className="flex items-center gap-2">
              <span className={`w-3.5 h-3.5 rounded-full border-2 shrink-0 ${scope === o.v ? "border-[#A3E635] bg-[#A3E635]" : "border-gray-500"}`} />
              <span className="font-semibold text-sm">{o.l}</span>
            </div>
            <p className="text-[11px] text-gray-500 mt-1 ml-5.5">{o.d}</p>
          </button>
        ))}
      </div>
      {scope === "bestimmte" && (
        <div className="rounded-lg border border-white/10 bg-black/30 p-3 max-h-60 overflow-y-auto" data-testid="scope-client-list">
          <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">{t("Kunden auswählen")} ({ids.length})</div>
          {clients.length === 0 && <p className="text-xs text-gray-500">{t("Keine Kunden vorhanden.")}</p>}
          {clients.map((c) => (
            <label key={c.id} data-testid={`scope-client-${c.id}`} className="flex items-center gap-2.5 py-1.5 cursor-pointer hover:bg-white/[0.02] rounded px-1">
              <input type="checkbox" checked={ids.includes(c.id)} onChange={() => toggle(c.id)} className="accent-[#A3E635] w-4 h-4" />
              <span className="text-sm">{c.company_name}</span>
            </label>
          ))}
        </div>
      )}
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{t("Abbrechen")}</button>
        <button onClick={submit} disabled={busy} data-testid="scope-submit" className="bg-[#A3E635] text-black font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />} {t("Speichern")}</button>
      </div>
      <style>{INP_STYLE}</style>
    </Modal>
  );
}

const CONF_BTN = { red: "bg-red-500 hover:bg-red-400 text-white", amber: "bg-amber-500 hover:bg-amber-400 text-black", blue: "bg-sky-500 hover:bg-sky-400 text-white" };
export function ConfirmModal({ t, title, msg, label, tone = "amber", onYes, onClose }) {
  const [busy, setBusy] = useState(false);
  return (
    <Modal title={title} onClose={onClose} testid="confirm-modal">
      <div className="flex items-start gap-3 mb-5">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${tone === "red" ? "bg-red-500/10" : tone === "blue" ? "bg-sky-500/10" : "bg-amber-500/10"}`}>
          <AlertTriangle className={`w-5 h-5 ${tone === "red" ? "text-red-400" : tone === "blue" ? "text-sky-300" : "text-amber-400"}`} />
        </div>
        <p className="text-sm text-gray-300">{msg}</p>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{t("Abbrechen")}</button>
        <button onClick={async () => { setBusy(true); await onYes(); onClose(); }} disabled={busy} data-testid="confirm-yes" className={`font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50 ${CONF_BTN[tone]}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {label}</button>
      </div>
    </Modal>
  );
}

export function TempPwModal({ t, email, password, emailSent, onClose }) {
  const copy = () => { navigator.clipboard?.writeText(password); toast.success(t("Kopiert")); };
  return (
    <Modal title={t("Temporäres Passwort")} onClose={onClose} testid="temppw-modal">
      <p className="text-sm text-gray-400 mb-1">{email}</p>
      <p className="text-xs text-gray-500 mb-4">{t("Bitte sicher an den Benutzer übermitteln. E-Mail-Versand erfolgt nur, wenn aktiv.")}</p>
      <div className="flex items-center gap-2 bg-black/50 border border-white/10 rounded-lg px-3 py-2.5">
        <code className="flex-1 font-mono text-[#A3E635] text-sm truncate" data-testid="temppw-value">{password}</code>
        <button onClick={copy} data-testid="temppw-copy" className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/5"><Copy className="w-4 h-4" /></button>
      </div>
      <div className="flex justify-end mt-5"><button onClick={onClose} className="bg-white/5 hover:bg-white/10 border border-white/10 px-4 py-2 rounded-lg text-sm">{t("Schliessen")}</button></div>
    </Modal>
  );
}
